// Real People · Real Results — testimonial showcase strip.
// Drops in below the hero on the homepage AND as the primary block on /results.
// Asset images live in /public/assets/testimonials/{slug}-card.jpg
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { useLeadModal } from "@/components/shared/LeadProvider";

const RED  = "#E11D2A";
const NAVY = "#07111F";

export const TESTIMONIALS = [
  { slug: "sarah",    name: "Sarah T.",    lost: "130 lbs" },
  { slug: "jennifer", name: "Jennifer K.", lost: "100 lbs" },
  { slug: "amanda",   name: "Amanda L.",   lost: "60 lbs"  },
  { slug: "michael",  name: "Michael B.",  lost: "80 lbs"  },
  { slug: "david",    name: "David R.",    lost: "120 lbs" },
  { slug: "chris",    name: "Chris D.",    lost: "70 lbs"  },
  { slug: "james",    name: "James P.",    lost: "110 lbs" },
  { slug: "robert",   name: "Robert M.",   lost: "110 lbs" },
];

export default function RealResults({ compact = false, limit }) {
  const { open } = useLeadModal();
  const list = limit ? TESTIMONIALS.slice(0, limit) : TESTIMONIALS;

  return (
    <section
      id="real-results"
      className="relative overflow-hidden"
      style={{ background: NAVY }}
      data-testid="real-results"
    >
      {/* Top accent stripe */}
      <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${RED} 0%, ${RED} 50%, #FFFFFF 100%)` }} />

      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-14 md:py-20">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <p className="font-extrabold tracking-[0.22em] text-[11px]" style={{ color: RED }}>
            ★ REAL PEOPLE · REAL RESULTS ★
          </p>
          <h2 className="font-heavy text-white leading-[0.95] mt-3"
            style={{ fontSize: "clamp(34px, 5.5vw, 72px)" }}>
            THIS COULD BE <span style={{ color: RED }}>YOU.</span>
          </h2>
          <p className="text-white/65 mt-4 max-w-2xl mx-auto text-[14px] sm:text-[15px] leading-relaxed">
            Real BestRx patients. Real transformations. Provider-guided weight loss programs with
            licensed review &amp; pharmacy fulfillment — delivered direct to your door.
          </p>
        </div>

        {/* Grid */}
        <div className={`grid gap-5 ${compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}
          data-testid="results-grid">
          {list.map((t) => (
            <article
              key={t.slug}
              className="bg-white overflow-hidden rounded-sm border-2 transition-transform hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(225,29,42,0.30)]"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`testimonial-${t.slug}`}
            >
              <img
                src={`/assets/testimonials/${t.slug}-card.jpg`}
                alt={`${t.name} — lost ${t.lost} with BestRx`}
                className="w-full h-auto block"
                loading="lazy"
                draggable={false}
              />
            </article>
          ))}
        </div>

        {/* Disclaimer */}
        <p className="text-center text-white/40 text-[11px] mt-8 max-w-3xl mx-auto leading-relaxed">
          *Results vary. Individual outcomes depend on lifestyle, diet, exercise, starting weight, and adherence
          to the provider-prescribed program.
        </p>

        {/* CTA strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => open({ source: "real-results" })}
            data-testid="results-cta-assessment"
            className="inline-flex items-center gap-2 px-7 py-4 font-heavy text-white text-[13px] tracking-[0.14em] uppercase"
            style={{ background: RED, boxShadow: "0 14px 30px rgba(225,29,42,0.45)" }}
          >
            Start My Assessment <ArrowRight size={16} strokeWidth={2.6} />
          </button>
          <Link
            to="/best/tirzepatide"
            data-testid="results-cta-explore"
            className="inline-flex items-center gap-2 px-7 py-4 font-heavy text-[13px] tracking-[0.14em] uppercase border-2 border-white text-white hover:bg-white hover:text-black transition-colors"
          >
            Explore Programs
          </Link>
        </div>

        {/* Trust footer */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-white/55 text-[10.5px] tracking-[0.18em] font-extrabold">
          <span className="inline-flex items-center gap-1.5"><Star size={11} fill="currentColor" style={{ color: RED }} /> LICENSED PROVIDERS</span>
          <span className="inline-flex items-center gap-1.5"><Star size={11} fill="currentColor" style={{ color: RED }} /> 4.9 / 5 PATIENT RATING</span>
          <span className="inline-flex items-center gap-1.5"><Star size={11} fill="currentColor" style={{ color: RED }} /> PHARMACY FULFILLED</span>
          <span className="inline-flex items-center gap-1.5"><Star size={11} fill="currentColor" style={{ color: RED }} /> RESULTS THAT LAST</span>
        </div>
      </div>
    </section>
  );
}
